import { DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';

import { Skeleton } from 'primeng/skeleton';

import { SelectableWidget } from '@components/selectable-widget/selectable-widget.component';
import { CurrencyAmountComponent } from '@cxc/ui-kit/components/currency-amount';

import { CurrencyState } from '@services/state/currency-state/currency.state';
import { OverviewResumeState } from '@services/state/overview-resume-state/overview-resume.state';

import { portfolioShare } from '@utils/lender-share';

/** El originador con más compromiso, y cuánto. */
export interface TopConcentration {
  name: string;
  amount: number;
}

/**
 * Paso 1 del tab Portfolio: **cuán grande es el portafolio y cuánto es tuyo**
 * (CA1 de LEN-2663).
 *
 * Cuatro indicadores, en orden de lectura: cuántas facilities, cuánto suman,
 * qué parte es del lender, y quién concentra más. El conteo abre la fila a
 * propósito -- cuántas antes de cuánto dinero, igual que en `core-metrics`.
 *
 * **Un dato que falta se pinta `—`, nunca `$0`.** Cero es una afirmación y
 * casi siempre es falsa: lo que pasa es que nadie lo capturó.
 */
@Component({
  selector: 'portfolio-kpis',
  standalone: true,
  imports: [
    DecimalPipe,
    UpperCasePipe,
    Skeleton,
    SelectableWidget,
    CurrencyAmountComponent,
  ],
  templateUrl: './portfolio-kpis.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioKpis {
  private state = inject(OverviewResumeState);

  /**
   * Los montos guardados son MXN (LEN-1808) salvo que el usuario elija otra.
   * El fallback vive en `CurrencyState`, compartido con las fichas de
   * facility, para que el Overview y una facility nunca muestren monedas
   * distintas (MR !1265 S2).
   */
  currency = inject(CurrencyState).displayCurrency;

  /**
   * El modo Export llega en LEN-2932. Las tarjetas ya van envueltas en
   * `selectable-widget` para no tener que volver a tocarlas, pero quien sabe
   * qué hay seleccionado es la página, no este paso, así que el cableado se
   * agrega cuando exista esa página -- no un `input` que hoy nadie pasa.
   */
  exportMode = input<boolean>(false);
  selectedWidgets = input<HTMLElement[]>([]);
  widgetToggle = output<{ checked: boolean; el: HTMLElement }>();

  /**
   * El payload todavía no llegó para la selección en la que está el usuario.
   * El estado limpia `resume` en cuanto cambia la selección, así que esto
   * cubre tanto la primera carga como el cambio de lender (CA18).
   *
   * Hace falta separarlo del dato ausente: pintar "Participation unknown"
   * mientras carga es una **afirmación falsa**, igual que un `$0` -- dice que
   * el dato no existe cuando todavía no se sabe.
   */
  loading = computed(
    () =>
      this.state.pendingSelection() !== undefined &&
      this.state.coreMetrics() === undefined &&
      !this.state.failed()
  );

  /** Una entrada por fideicomiso: el servidor ya fusionó los tramos. */
  facilities = computed(() => this.state.coreMetrics()?.operations?.length);

  /**
   * Cuántos originadores distintos hay detrás de esas facilities (CA1). No es
   * el mismo número: un originador puede tener varias.
   */
  originators = computed(() => {
    const nombres = new Set<string>();
    for (const operation of this.state.operations() ?? []) {
      for (const originator of operation.originators ?? []) {
        const name = originator.name?.trim();
        if (name) nombres.add(name);
      }
    }
    return nombres.size || undefined;
  });

  totalFacilitySize = computed(
    () => this.state.coreMetrics()?.total_credit_amount
  );

  /**
   * Compromiso ÷ monto máximo, **sumando sólo las facilities que tienen los
   * dos montos capturados**.
   *
   * La regla vive en `lender-share.ts` -- "one rule for every screen" -- y no
   * acá: la tarjeta de Insights muestra este mismo porcentaje, y mientras cada
   * pantalla lo calculaba por su cuenta el Overview llegó a mostrar dos
   * números distintos para el mismo portafolio (LEN-3334).
   *
   * `undefined` cuando ninguna tiene los dos montos -- la tarjeta pinta `—` y
   * dice por qué. Una sindicada sin porcentaje **no se asume 100%**: adivinar
   * ahí infla la exposición que el analista cree tener.
   */
  private share = computed(() =>
    portfolioShare(this.state.coreMetrics()?.operations)
  );

  participationPct = computed(() => this.share().pct);

  /**
   * Las facilities que el porcentaje **no** pudo medir -- sin compromiso, sin
   * techo usable, o caso 05. Con alguna, "of the facilities you are in" diría
   * de más: el número habla de una parte (MR !1366 S3).
   */
  participationExcluded = computed(() => this.share().excluded);
  participationCounted = computed(() => this.share().counted);

  /**
   * El originador con más exposición **sumando todas sus facilities**, no la
   * facility más grande (CA1).
   *
   * Hay que cruzar dos arreglos del mismo payload porque ninguno trae las dos
   * mitades: el compromiso vive en `core_metrics.operations[]` y el
   * originador en `operations[]` del resume. Se cruzan por `trust_number`.
   *
   * Los montos salen de core-metrics y no del resume a propósito: el resume
   * agrupa los tramos y le borra la participación (LEN-2799 esc. 5).
   */
  topConcentration = computed<TopConcentration | undefined>(() => {
    const committedByTrust = new Map<string, number>();
    for (const op of this.state.coreMetrics()?.operations ?? []) {
      if (op.committed_amount == null) continue;
      committedByTrust.set(
        op.trust_number,
        (committedByTrust.get(op.trust_number) ?? 0) + op.committed_amount
      );
    }
    if (!committedByTrust.size) return undefined;

    const byOriginator = new Map<string, number>();
    // Un fideicomiso puede aparecer más de una vez en el resume -- el mismo
    // lender en dos tramos (CA9). El compromiso es del fideicomiso, así que
    // se cuenta una sola vez o el originador queda inflado.
    const contados = new Set<string>();
    for (const operation of this.state.operations() ?? []) {
      const committed = committedByTrust.get(operation.trust_number);
      if (committed === undefined) continue;
      if (contados.has(operation.trust_number)) continue;
      contados.add(operation.trust_number);
      // Un fideicomiso puede listar varios originadores; el compromiso es del
      // fideicomiso, así que cuenta entero para cada uno. Sumarlo partido
      // inventaría un reparto que el dato no dice.
      for (const originator of operation.originators ?? []) {
        const name = originator.name?.trim();
        if (!name) continue;
        byOriginator.set(name, (byOriginator.get(name) ?? 0) + committed);
      }
    }

    let top: TopConcentration | undefined;
    for (const [name, amount] of byOriginator) {
      if (!top || amount > top.amount) top = { name, amount };
    }
    return top;
  });

  isWidgetSelected(el: HTMLElement): boolean {
    return this.selectedWidgets().includes(el);
  }
}
